import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  useMediaQuery,
} from '@mui/material';
import {
  Check as CheckIcon,
  CloudUpload as CloudUploadIcon,
  ContentCopy as ContentCopyIcon,
  ContentCut as ContentCutIcon,
  ContentPaste as ContentPasteIcon,
  CreateNewFolder as CreateNewFolderIcon,
  DeleteOutline as DeleteOutlineIcon,
  DriveFileRenameOutline as RenameIcon,
  DriveFolderUpload as DriveFolderUploadIcon,
  InfoOutlined as InfoOutlinedIcon,
  Link as LinkIcon,
  EventAvailable as EventAvailableIcon,
  FileDownload as FileDownloadIcon,
  FolderOpen as FolderOpenIcon,
  FolderZip as FolderZipIcon,
  ChevronRight as ChevronRightIcon,
  UploadFile as UploadFileIcon,
  TableChart as TableChartIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { SET_CURRENT_VERSION_LABEL } from '../../store/subMatFolder';
import { folderAcceptsSpec } from './SubMatFolderSpecDialog';

const iconSx = { minWidth: 36 };
const iconFont = { fontSize: 20 };

const CtxItem = ({
  icon,
  label,
  onClick,
  onMouseEnter,
  onMouseLeave,
  danger = false,
  disabled = false,
  endIcon = null,
  uploadItem = false,
}) => (
  <MenuItem
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    disabled={disabled}
    data-submat-upload={uploadItem ? '' : undefined}
    sx={danger ? { color: 'error.main' } : undefined}
  >
    <ListItemIcon sx={{ ...iconSx, color: danger ? 'error.main' : 'inherit' }}>
      {icon}
    </ListItemIcon>
    <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
    {endIcon}
  </MenuItem>
);

/**
 * Modal 없는 컨텍스트 메뉴 — 다른 노드 우클릭 시 한 번에 교체(윈도우 탐색기형).
 * @param {object|null} state — { mouseX, mouseY, kind, folder?, file? }
 */
const SubMatFolderContextMenu = ({
  state,
  onClose,
  onOpenFolder,
  onRenameFolder,
  onFolderSpec,
  onRenameFile,
  onCreateChildFolder,
  onDeleteFolder,
  onCopyFolder,
  onCutFolder,
  onPasteFolder,
  canPaste,
  onCreateTestComparison,
  onUpload,
  onDownloadBundle,
  onUploadBundle,
  onUploadZip,
  onViewFile,
  onDownloadFile,
  onSetExpiryFile,
  onSetCurrentFile,
  onDeleteFile,
  canAddChild,
  canRenameFolder,
  canDeleteFolder,
  canCutCopy,
  uploadable,
  onAttachAdditional,
  onDetachAdditional,
  fileReadOnly = false,
}) => {
  const open = Boolean(state);
  const isMobile = useMediaQuery((muiTheme) => muiTheme.breakpoints.down('md'));
  const [uploadAnchor, setUploadAnchor] = useState(null);
  const uploadTimerRef = useRef(null);

  const clearUploadTimer = () => {
    if (uploadTimerRef.current) {
      clearTimeout(uploadTimerRef.current);
      uploadTimerRef.current = null;
    }
  };

  const scheduleUploadOpen = (el) => {
    clearUploadTimer();
    uploadTimerRef.current = setTimeout(() => {
      const rect = el.getBoundingClientRect();
      const menuWidth = 196;
      let left = rect.right - 6;
      if (left + menuWidth > window.innerWidth - 8) left = Math.max(8, rect.left - menuWidth);
      setUploadAnchor({ top: rect.top, left });
    }, 450);
  };

  const scheduleUploadClose = () => {
    clearUploadTimer();
    uploadTimerRef.current = setTimeout(() => setUploadAnchor(null), 180);
  };

  useEffect(() => {
    clearUploadTimer();
    setUploadAnchor(null);
  }, [state]);

  useEffect(() => () => clearUploadTimer(), []);

  // 메뉴가 열린 동안 브라우저 기본 메뉴만 막고, 절대 닫지 않음(노드가 setCtxMenu로 교체)
  useEffect(() => {
    if (!open) return undefined;
    const preventBrowserMenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener('contextmenu', preventBrowserMenu, true);
    return () => document.removeEventListener('contextmenu', preventBrowserMenu, true);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const items = useMemo(() => {
    if (!state) return null;
    const { kind, folder, file } = state;
    if (kind === 'folder' && folder?.linkView) {
      return (
        <>
          <CtxItem
            icon={<FolderOpenIcon sx={iconFont} />}
            label="열기"
            onClick={() => { onOpenFolder(folder); onClose(); }}
          />
          <CtxItem
            icon={<LinkIcon sx={iconFont} />}
            label="여기서 빼기"
            onClick={() => { onDetachAdditional?.(folder); onClose(); }}
          />
        </>
      );
    }
    if (kind === 'folder' && folder) {
      const showSpec = folderAcceptsSpec(folder);
      return (
        <>
          {showSpec && (
            <CtxItem
              icon={<InfoOutlinedIcon sx={iconFont} />}
              label="속성"
              onClick={() => { onFolderSpec?.(folder); onClose(); }}
            />
          )}
          {showSpec && <Divider />}
          <CtxItem
            icon={<FolderOpenIcon sx={iconFont} />}
            label="열기"
            onClick={() => { onOpenFolder(folder); onClose(); }}
          />
          {!folder.isSystem && folder.folderLevel !== 0 && (
            <CtxItem
              icon={<LinkIcon sx={iconFont} />}
              label="추가 서류로 붙이기"
              onClick={() => { onAttachAdditional?.(folder); onClose(); }}
            />
          )}
          {canRenameFolder && (
            <CtxItem
              icon={<RenameIcon sx={iconFont} />}
              label="이름 바꾸기"
              onClick={() => { onRenameFolder(folder); onClose(); }}
            />
          )}
          {canAddChild && (
            <CtxItem
              icon={<CreateNewFolderIcon sx={iconFont} />}
              label="하위 폴더 만들기"
              onClick={() => { onCreateChildFolder(folder); onClose(); }}
            />
          )}
          <Divider />
          {canCutCopy && (
            <>
              <CtxItem
                icon={<ContentCopyIcon sx={iconFont} />}
                label="복사"
                onClick={() => { onCopyFolder?.(folder); onClose(); }}
              />
              <CtxItem
                icon={<ContentCutIcon sx={iconFont} />}
                label="잘라내기"
                onClick={() => { onCutFolder?.(folder); onClose(); }}
              />
            </>
          )}
          <CtxItem
            icon={<ContentPasteIcon sx={iconFont} />}
            label="붙여넣기"
            disabled={!canPaste}
            onClick={() => { onPasteFolder?.(folder); onClose(); }}
          />
          <Divider />
          <CtxItem
            icon={<FolderZipIcon sx={iconFont} />}
            label="zip으로 다운로드"
            onClick={() => { onDownloadBundle?.(folder); onClose(); }}
          />
          {canDeleteFolder && !folder.isSystem && (
            <>
              <Divider />
              <CtxItem
                icon={<DeleteOutlineIcon sx={iconFont} />}
                label="폴더 삭제"
                danger
                onClick={() => { onDeleteFolder(folder); onClose(); }}
              />
            </>
          )}
        </>
      );
    }
    if (kind === 'file' && file) {
      const fileFolder = folder || null;
      if (fileReadOnly || fileFolder?.linkView) {
        return (
          <>
            <CtxItem
              icon={<VisibilityIcon sx={iconFont} />}
              label="미리보기"
              onClick={() => { onViewFile(file); onClose(); }}
            />
            <CtxItem
              icon={<FileDownloadIcon sx={iconFont} />}
              label="다운로드"
              onClick={() => { onDownloadFile(file); onClose(); }}
            />
          </>
        );
      }
      const isDocument = !file.fileRole || file.fileRole === 'DOCUMENT';
      // 성적서(TEST)·기타(ETC) DOCUMENT — 폴더 속성 없이 파일 기준으로 대비표 허용
      const canCreateComparison = !isMobile
        && (fileFolder?.rootKind === 'TEST' || fileFolder?.rootKind === 'ETC')
        && !(fileFolder.isSystem && fileFolder.folderLevel === 0)
        && isDocument;
      return (
        <>
          <CtxItem
            icon={<VisibilityIcon sx={iconFont} />}
            label="미리보기"
            onClick={() => { onViewFile(file); onClose(); }}
          />
          {isDocument && (
            <CtxItem
              icon={<EventAvailableIcon sx={iconFont} />}
              label="만료일 설정"
              onClick={() => { onSetExpiryFile?.(fileFolder, file); onClose(); }}
            />
          )}
          <CtxItem
            icon={<FileDownloadIcon sx={iconFont} />}
            label="다운로드"
            onClick={() => { onDownloadFile(file); onClose(); }}
          />
          {isDocument && (
            <CtxItem
              icon={<RenameIcon sx={iconFont} />}
              label="이름 바꾸기"
              onClick={() => { onRenameFile?.(fileFolder, file); onClose(); }}
            />
          )}
          {!file.isCurrent && file.fileRole !== 'TEST_COMPARISON' && (
            <CtxItem
              icon={<CheckIcon sx={iconFont} />}
              label={SET_CURRENT_VERSION_LABEL}
              onClick={() => { onSetCurrentFile(file); onClose(); }}
            />
          )}
          {canCreateComparison && (
            <>
              <Divider />
              <CtxItem
                icon={<TableChartIcon sx={iconFont} />}
                label={file.comparisonFile ? '시험성과대비표 수정' : '시험성과대비표 만들기'}
                onClick={() => { onCreateTestComparison?.(fileFolder, file); onClose(); }}
              />
            </>
          )}
          <Divider />
          <CtxItem
            icon={<DeleteOutlineIcon sx={iconFont} />}
            label="삭제"
            danger
            onClick={() => { onDeleteFile(file); onClose(); }}
          />
        </>
      );
    }
    if (kind === 'pane' && folder?.linkView) {
      return (
        <CtxItem
          icon={<LinkIcon sx={iconFont} />}
          label="여기서 빼기"
          onClick={() => { onDetachAdditional?.(folder); onClose(); }}
        />
      );
    }
    if (kind === 'pane' && folder) {
      const showSpec = folderAcceptsSpec(folder);
      return (
        <>
          {showSpec && (
            <CtxItem
              icon={<InfoOutlinedIcon sx={iconFont} />}
              label="속성"
              onClick={() => { onFolderSpec?.(folder); onClose(); }}
            />
          )}
          {showSpec && <Divider />}
          {!folder.isSystem && folder.folderLevel !== 0 && (
            <CtxItem
              icon={<LinkIcon sx={iconFont} />}
              label="추가 서류로 붙이기"
              onClick={() => { onAttachAdditional?.(folder); onClose(); }}
            />
          )}
          {canAddChild && (
            <CtxItem
              icon={<CreateNewFolderIcon sx={iconFont} />}
              label="하위 폴더 만들기"
              onClick={() => { onCreateChildFolder(folder); onClose(); }}
            />
          )}
          <CtxItem
            icon={<ContentPasteIcon sx={iconFont} />}
            label="붙여넣기"
            disabled={!canPaste}
            onClick={() => { onPasteFolder?.(folder); onClose(); }}
          />
          <Divider />
          {uploadable && (
            <CtxItem
              icon={<CloudUploadIcon sx={iconFont} />}
              label="PDF 업로드"
              onClick={() => { onUpload(); onClose(); }}
            />
          )}
          <CtxItem
            icon={<DriveFolderUploadIcon sx={iconFont} />}
            label="업로드"
            uploadItem
            endIcon={<ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            onMouseEnter={(e) => scheduleUploadOpen(e.currentTarget)}
            onMouseLeave={scheduleUploadClose}
          />
          <CtxItem
            icon={<FolderZipIcon sx={iconFont} />}
            label="zip으로 다운로드"
            onClick={() => { onDownloadBundle?.(folder); onClose(); }}
          />
        </>
      );
    }
    return null;
  }, [
    state,
    onClose,
    onOpenFolder,
    onRenameFolder,
    onFolderSpec,
    onRenameFile,
    onCreateChildFolder,
    onDeleteFolder,
    onUpload,
    onDownloadBundle,
    onUploadBundle,
    onUploadZip,
    onViewFile,
    onDownloadFile,
    onSetCurrentFile,
    onDeleteFile,
    canAddChild,
    canRenameFolder,
    canDeleteFolder,
    uploadable,
    canCutCopy,
    canPaste,
    onCopyFolder,
    onCutFolder,
    onPasteFolder,
    onCreateTestComparison,
    isMobile,
    onAttachAdditional,
    onDetachAdditional,
    fileReadOnly,
    scheduleUploadOpen,
    scheduleUploadClose,
  ]);

  const paperRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // 탐색기 빈 영역은 pointerdown을 취소해 mousedown이 안 나온다.
  // 버블 단계 ClickAway는 그때 못 닫으므로, 캡처 단계에서 바깥 클릭을 닫는다.
  // 우클릭(button 2)은 노드 contextmenu가 메뉴를 교체하므로 여기서 닫지 않는다.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (e.button === 2) return;
      const target = e.target;
      if (target instanceof Element && target.closest('[data-submat-menu]')) return;
      onCloseRef.current?.();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  // 우클릭 좌표 기준 배치 후, 뷰포트 밖으로 나가지 않게 보정
  useLayoutEffect(() => {
    if (!state || !paperRef.current) return;
    const el = paperRef.current;
    const pad = 8;
    const maxH = window.innerHeight - pad * 2;
    const maxW = window.innerWidth - pad * 2;

    el.style.maxHeight = '';
    el.style.overflowY = '';

    let top = state.mouseY;
    let left = state.mouseX;
    let h = el.offsetHeight;
    let w = el.offsetWidth;

    if (h > maxH) {
      el.style.maxHeight = `${maxH}px`;
      el.style.overflowY = 'auto';
      h = maxH;
      top = pad;
    } else if (top + h > window.innerHeight - pad) {
      top = Math.max(pad, window.innerHeight - h - pad);
    }
    if (top < pad) top = pad;

    if (w > maxW) {
      left = pad;
    } else if (left + w > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - w - pad);
    }
    if (left < pad) left = pad;

    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }, [state, items]);

  if (!state || !items) return null;

  const { mouseX, mouseY } = state;

  return createPortal(
    <Paper
      ref={paperRef}
      data-submat-menu=""
      elevation={8}
      sx={{
        position: 'fixed',
        top: mouseY,
        left: mouseX,
        zIndex: (t) => t.zIndex.modal,
        minWidth: 200,
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <MenuList
        dense
        autoFocusItem={false}
        onMouseOver={(e) => {
          if (e.target.closest?.('[data-submat-upload]')) return;
          clearUploadTimer();
          setUploadAnchor(null);
        }}
      >
        {items}
      </MenuList>
      {uploadAnchor && state?.kind === 'pane' && state.folder && (
        <Paper
          data-submat-menu=""
          elevation={8}
          sx={{
            position: 'fixed',
            top: uploadAnchor.top,
            left: uploadAnchor.left,
            zIndex: (t) => t.zIndex.modal,
            minWidth: 188,
          }}
          onContextMenu={(e) => e.preventDefault()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={clearUploadTimer}
          onMouseLeave={scheduleUploadClose}
        >
          <MenuList dense>
            <CtxItem
              icon={<UploadFileIcon sx={iconFont} />}
              label="zip으로 업로드"
              onClick={() => { onUploadZip?.(state.folder); onClose(); }}
            />
            <CtxItem
              icon={<DriveFolderUploadIcon sx={iconFont} />}
              label="폴더 업로드"
              onClick={() => { onUploadBundle?.(state.folder); onClose(); }}
            />
          </MenuList>
        </Paper>
      )}
    </Paper>,
    document.body,
  );
};

export default SubMatFolderContextMenu;
